/**
 * Sources rail — ranked, digested source cards (Ask view, right column). Only the
 * active card expands to a short snippet + "View in document"; the rest stay
 * collapsed (click a header to activate/expand). This is the key change from the
 * old "every source dumps its full chunk" rail.
 */
import { ExternalLink } from "lucide-react";
import { DOC_TYPE_META } from "../kb-ui";
import type { KbCitation } from "../types";

function SourceCard({
	citation,
	active,
	onActivate,
	onView,
}: {
	citation: KbCitation;
	active: boolean;
	onActivate: () => void;
	onView: () => void;
}) {
	const meta = DOC_TYPE_META[citation.docType];
	const TypeIcon = meta.icon;
	return (
		<div
			className={`rounded-lg border bg-surface-1 ${
				active ? "border-primary/40" : "border-hairline hover:border-hairline-strong"
			}`}
		>
			<button
				type="button"
				onClick={onActivate}
				aria-expanded={active}
				className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
			>
				<span
					className="flex size-[18px] flex-none items-center justify-center rounded text-[10px] font-semibold"
					style={
						active
							? { background: "var(--color-primary)", color: "var(--color-on-primary)" }
							: {
									background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
									color: "var(--color-primary-hover)",
								}
					}
				>
					{citation.n}
				</span>
				<TypeIcon className="size-3.5 flex-none" style={{ color: meta.tint }} />
				<span className="min-w-0 flex-1 truncate text-body-sm text-foreground">
					{citation.filename}
				</span>
			</button>
			<div className="-mt-1 flex items-center gap-2 px-3 pb-2 text-meta text-ink-subtle">
				<span className="font-mono">chunk #{citation.chunkIndex}</span>
				<span className="flex-1" />
				<span className="inline-flex items-center gap-1">
					<span className="block h-1 w-10 overflow-hidden rounded-full bg-surface-3">
						<span
							className="block h-full rounded-full bg-primary"
							style={{ width: `${Math.min(100, citation.score * 2)}%` }}
						/>
					</span>
					<span className="tabular-nums">{citation.score}%</span>
				</span>
			</div>
			{active ? (
				<div className="border-t border-hairline px-3 py-2.5">
					<p className="line-clamp-3 text-body-sm leading-snug text-ink-muted">
						{citation.snippet}
					</p>
					<button
						type="button"
						onClick={onView}
						className="mt-2 inline-flex items-center gap-1.5 text-meta font-medium text-primary-hover hover:underline"
					>
						<ExternalLink className="size-3" /> View in document
					</button>
				</div>
			) : null}
		</div>
	);
}

export function SourceRail({
	citations,
	activeCite,
	onActivate,
	onView,
}: {
	citations: KbCitation[];
	activeCite: number | null;
	onActivate: (c: KbCitation) => void;
	onView: (c: KbCitation) => void;
}) {
	return (
		<aside className="overflow-y-auto bg-canvas">
			<div className="sticky top-0 z-10 flex items-center justify-between border-b border-hairline bg-canvas/95 px-4 py-3 backdrop-blur">
				<span className="text-eyebrow uppercase text-ink-subtle">Sources · {citations.length}</span>
				<span className="text-meta text-ink-subtle">ranked by relevance</span>
			</div>
			<div className="space-y-2.5 p-3">
				{citations.map((c, i) => (
					<SourceCard
						key={`${c.documentId}-${c.chunkIndex}`}
						citation={c}
						active={activeCite != null ? activeCite === c.n : i === 0}
						onActivate={() => onActivate(c)}
						onView={() => onView(c)}
					/>
				))}
			</div>
		</aside>
	);
}
